import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Plus, Pencil, Trash2, X, CheckSquare, Square, Crown, ScrollText, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import { API } from './shared';

const EMPTY = { first_name: '', last_name: '', email: '', password: '', allowed_tabs: [] };

export function TeamTab({ theme, tabs = [] }) {
  const light = theme === 'light';
  const [view, setView] = useState('admins');
  return (
    <div data-testid="team-tab">
      <div className={`inline-flex rounded-lg p-0.5 mb-5 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
        {[['admins', 'Admins', Users], ['journal', "Journal d'activité", ScrollText]].map(([id, lbl, Icon]) => (
          <button key={id} data-testid={`team-view-${id}`} onClick={() => setView(id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-md transition-colors ${
              view === id ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500 hover:text-gray-800' : 'text-white/40 hover:text-white'}`}>
            <Icon size={13} /> {lbl}
          </button>
        ))}
      </div>
      {view === 'admins' ? <AdminsView theme={theme} tabs={tabs} /> : <JournalView theme={theme} />}
    </div>
  );
}

function JournalView({ theme }) {
  const light = theme === 'light';
  const [logs, setLogs] = useState(null);
  const [q, setQ] = useState('');
  const [days, setDays] = useState(30);
  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';
  const hintCls = light ? 'text-gray-400' : 'text-white/35';

  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/admin/audit-log`, { params: { days, q }, withCredentials: true })
        .then(r => setLogs(r.data)).catch(() => setLogs([]));
    }, 350);
    return () => clearTimeout(t);
  }, [q, days]);

  const badge = (action) => action === 'Suppression' ? 'bg-red-500/15 text-red-400'
    : action === 'Modification' ? 'bg-blue-500/15 text-blue-400'
    : action === 'Connexion' ? 'bg-emerald-500/15 text-emerald-400'
    : 'bg-[#E9641A]/15 text-[#E9641A]';

  return (
    <div data-testid="team-journal">
      <h1 className={`text-2xl font-bold mb-1 ${light ? 'text-gray-900' : 'text-white'}`}>Journal d'activité</h1>
      <p className={`text-xs mb-5 ${hintCls}`}>Trace de toutes les actions effectuées dans le dashboard : connexions, créations, modifications et suppressions (90 jours conservés).</p>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className={`flex items-center gap-2 rounded-lg px-3 py-2 flex-1 max-w-sm border ${light ? 'bg-white border-gray-200' : 'bg-white/5 border-white/8'}`}>
          <Search size={13} className={hintCls} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher (admin, action, onglet…)" data-testid="journal-search"
            className={`bg-transparent text-xs outline-none flex-1 ${light ? 'text-gray-800' : 'text-white'} placeholder:opacity-40`} />
        </div>
        <div className={`flex rounded-lg p-0.5 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
          {[[7, '7 j'], [30, '30 j'], [90, '90 j']].map(([d, lbl]) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`journal-days-${d}`}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-md ${days === d ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500' : 'text-white/40'}`}>{lbl}</button>
          ))}
        </div>
        {logs && <span className={`text-[11px] ${hintCls}`} data-testid="journal-count">{logs.length} entrée{logs.length > 1 ? 's' : ''}</span>}
      </div>
      <div className={`rounded-xl border overflow-hidden ${cardCls}`}>
        <table className="w-full text-left">
          <thead>
            <tr className={`text-[10px] uppercase tracking-wider ${light ? 'text-gray-400 bg-gray-50' : 'text-white/30 bg-white/[0.03]'}`}>
              <th className="px-4 py-2.5 font-semibold">Date</th>
              <th className="px-4 py-2.5 font-semibold">Admin</th>
              <th className="px-4 py-2.5 font-semibold">Action</th>
              <th className="px-4 py-2.5 font-semibold">Onglet / zone</th>
              <th className="px-4 py-2.5 font-semibold">Détail</th>
            </tr>
          </thead>
          <tbody>
            {(logs || []).map(l => (
              <tr key={l.id} className={`border-t text-xs ${light ? 'border-gray-100' : 'border-white/5'}`} data-testid={`journal-row-${l.id}`}>
                <td className={`px-4 py-2.5 whitespace-nowrap ${hintCls}`}>{new Date(l.created_at).toLocaleString('fr-FR')}</td>
                <td className="px-4 py-2.5">
                  <p className={`font-semibold ${light ? 'text-gray-800' : 'text-white'}`}>{l.actor_name || '—'}</p>
                  <p className={`text-[10px] ${hintCls}`}>{l.actor_email}</p>
                </td>
                <td className="px-4 py-2.5"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${badge(l.action)}`}>{l.action}</span></td>
                <td className={`px-4 py-2.5 font-medium ${light ? 'text-gray-700' : 'text-white/70'}`}>{l.category}</td>
                <td className={`px-4 py-2.5 font-mono text-[10px] ${hintCls}`}>{l.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs && logs.length === 0 && <p className={`text-center text-sm py-10 ${hintCls}`}>Aucune activité sur cette période.</p>}
        {!logs && <p className={`text-center text-sm py-10 ${hintCls}`}>Chargement…</p>}
      </div>
    </div>
  );
}

function AdminsView({ theme, tabs = [] }) {
  const light = theme === 'light';
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState(null); // null | {..., editing?: email}
  const [saving, setSaving] = useState(false);

  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';
  const inputCls = `w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${light ? 'bg-white border border-gray-200 text-gray-800' : 'bg-white/5 border border-white/8 text-white'}`;
  const lblCls = `block text-[10px] mb-1 uppercase tracking-wider ${light ? 'text-gray-400' : 'text-white/35'}`;

  const load = () => axios.get(`${API}/admin/team`, { withCredentials: true }).then(r => setAdmins(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleTab = (id) => setForm(f => ({ ...f, allowed_tabs: f.allowed_tabs.includes(id) ? f.allowed_tabs.filter(t => t !== id) : [...f.allowed_tabs, id] }));
  const allChecked = form && tabs.every(t => form.allowed_tabs.includes(t.id));
  const checkAll = () => setForm(f => ({ ...f, allowed_tabs: allChecked ? [] : tabs.map(t => t.id) }));

  const openEdit = (a) => setForm({ first_name: a.first_name || '', last_name: a.last_name || '', email: a.email, password: '', allowed_tabs: a.allowed_tabs || tabs.map(t => t.id), editing: a.email });

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (form.editing) {
        const payload = { first_name: form.first_name, last_name: form.last_name, allowed_tabs: form.allowed_tabs };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/admin/team/${encodeURIComponent(form.editing)}`, payload, { withCredentials: true });
        toast.success('Admin mis à jour');
      } else {
        await axios.post(`${API}/admin/team`, form, { withCredentials: true });
        toast.success('Compte admin créé');
      }
      setForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer le compte admin de ${a.name || a.email} ?`)) return;
    try {
      await axios.delete(`${API}/admin/team/${encodeURIComponent(a.email)}`, { withCredentials: true });
      toast.success('Admin supprimé');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div data-testid="team-admins">
      <div className="flex items-center justify-between mb-1">
        <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Utilisateurs du dashboard</h1>
        <button onClick={() => setForm({ ...EMPTY })} data-testid="team-add-btn"
          className="flex items-center gap-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
          <Plus size={14} /> Nouvel admin
        </button>
      </div>
      <p className={`text-xs mb-6 ${light ? 'text-gray-400' : 'text-white/35'}`}>Créez des comptes admin et choisissez les onglets du dashboard auxquels chacun a accès.</p>

      <div className="space-y-2">
        {admins.map(a => (
          <div key={a.email} className={`flex items-center gap-3 rounded-xl border p-4 ${cardCls}`} data-testid={`team-row-${a.email}`}>
            <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-none ${a.is_superadmin ? 'bg-amber-400/15' : 'bg-[#E9641A]/15'}`}>
              {a.is_superadmin ? <Crown size={16} className="text-amber-400" /> : <Shield size={15} className="text-[#E9641A]" />}
            </span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${light ? 'text-gray-800' : 'text-white'}`}>
                {a.name || a.email}
                {a.is_superadmin && <span className="ml-2 text-[10px] font-bold text-amber-400 uppercase">Superadmin</span>}
              </p>
              <p className={`text-[11px] ${light ? 'text-gray-500' : 'text-white/45'}`}>{a.email}</p>
              <p className={`text-[10px] mt-0.5 ${light ? 'text-gray-400' : 'text-white/30'}`}>
                {a.is_superadmin ? 'Accès à tous les onglets' :
                  !a.allowed_tabs || a.allowed_tabs.length === tabs.length ? `Tous les onglets (${tabs.length})` :
                  `${a.allowed_tabs.length} onglet${a.allowed_tabs.length > 1 ? 's' : ''} : ${a.allowed_tabs.map(id => tabs.find(t => t.id === id)?.label || id).slice(0, 6).join(', ')}${a.allowed_tabs.length > 6 ? '…' : ''}`}
              </p>
            </div>
            {!a.is_superadmin && (
              <div className="flex items-center gap-1 flex-none">
                <button onClick={() => openEdit(a)} data-testid={`team-edit-${a.email}`}
                  className={`p-2 rounded-lg ${light ? 'text-gray-400 hover:text-gray-700 hover:bg-gray-100' : 'text-white/30 hover:text-white hover:bg-white/5'}`}><Pencil size={14} /></button>
                <button onClick={() => remove(a)} data-testid={`team-delete-${a.email}`}
                  className={`p-2 rounded-lg ${light ? 'text-gray-300 hover:text-red-500 hover:bg-red-50' : 'text-white/20 hover:text-red-400 hover:bg-red-500/10'}`}><Trash2 size={14} /></button>
              </div>
            )}
          </div>
        ))}
        {admins.length === 0 && <p className={`text-center text-sm py-8 ${light ? 'text-gray-400' : 'text-white/35'}`}>Chargement…</p>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setForm(null)} data-testid="team-form-modal">
          <div className={`rounded-xl border w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 ${cardCls}`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={`text-lg font-bold ${light ? 'text-gray-900' : 'text-white'}`}>{form.editing ? `Modifier ${form.editing}` : 'Nouvel admin'}</h3>
              <button onClick={() => setForm(null)} className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'} data-testid="team-form-close"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div><label className={lblCls}>Prénom</label>
                <input className={inputCls} value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} data-testid="team-firstname" /></div>
              <div><label className={lblCls}>Nom</label>
                <input className={inputCls} value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} data-testid="team-lastname" /></div>
              <div><label className={lblCls}>Email</label>
                <input className={inputCls} type="email" value={form.email} disabled={!!form.editing} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="team-email" /></div>
              <div><label className={lblCls}>{form.editing ? 'Nouveau mot de passe (laisser vide pour conserver)' : 'Mot de passe'}</label>
                <input className={inputCls} type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} data-testid="team-password" /></div>
            </div>
            <div className="flex items-center justify-between mb-2 mt-4">
              <label className={lblCls} style={{ marginBottom: 0 }}>Accès aux onglets ({form.allowed_tabs.length}/{tabs.length})</label>
              <button onClick={checkAll} data-testid="team-check-all"
                className="flex items-center gap-1.5 text-xs font-semibold text-[#E9641A] hover:text-[#FE975E]">
                {allChecked ? <CheckSquare size={14} /> : <Square size={14} />} {allChecked ? 'Tout décocher' : 'Cocher tous les onglets'}
              </button>
            </div>
            <div className={`grid grid-cols-2 md:grid-cols-3 gap-1.5 rounded-lg border p-3 mb-5 ${light ? 'border-gray-200 bg-gray-50' : 'border-white/8 bg-white/[0.02]'}`}>
              {tabs.map(t => {
                const on = form.allowed_tabs.includes(t.id);
                return (
                  <button key={t.id} onClick={() => toggleTab(t.id)} data-testid={`team-tabchk-${t.id}`}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-left transition-colors ${
                      on ? 'text-[#E9641A] font-semibold' : light ? 'text-gray-500 hover:text-gray-800' : 'text-white/45 hover:text-white'}`}>
                    {on ? <CheckSquare size={14} className="flex-none" /> : <Square size={14} className="flex-none" />}
                    <span className="truncate">{t.label}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={save} disabled={saving} data-testid="team-save-btn"
              className="w-full bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-colors disabled:opacity-40">
              {saving ? 'Enregistrement…' : form.editing ? 'Enregistrer les modifications' : 'Créer le compte admin'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
